import React, { useState, useRef, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Form, Button, Card, Space, Row, Col, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { CustomForm } from "ui/Form"
import { useWatch } from "antd/es/form/Form"
import { useSelector } from "react-redux"
import dayjs from "dayjs"
import ClientDetail from "./ClientDetail"
import ExperienceDetail from "./ExperienceDetail"
import PaymentMethod from "./PaymentMethod"
import PriceBreakdown from "./PriceBreakdown"
import PromoCodeInput from "ui/PromoCodeInput"
import Stepper from "./Stepper"
import styles from "./BookingForm.module.css"
import { Text, Title } from "ui/Typography"
import AddOnsSelector from "components/bookings/AddOnsSelector"
import { calculateAddOnsTotal } from "components/bookings/helpers"
import { calculateBookingTotal } from "../helpers/calculateBookingTotal"
import { resolveEffectiveDiscountPromos } from "utils/discountUtils"
import { getCardFeeFraction } from "utils/bookingCalculations"

const STEP1_BASE_FIELDS = ["experience", "date", "duration", "time", ["main", "adults"], "guestTotal"]
const STEP1_NEW_CLIENT_FIELDS = ["firstName", "lastName", "email", "phone_number"]
const STEP1_EXISTING_CLIENT_FIELDS = ["existing_client", "email"]

const BookingForm = ({
  form,
  bookingUuid,
  bookAgain,
  booking,
  onSubmit,
  onCancelModal,
  filterExperienceIds,
  appliedPromos,
  onPromoApplied,
  onPromoRemoved,
  onClearPromos,
  onValuesChange,
  bookingSelections,
  updateBookingSelections
}) => {
  const { t } = useTranslation()
  const [existingClientId, setExistingClientId] = useState(null)
  const [currentStep, setCurrentStep] = useState(1)
  const scrollContainerRef = useRef(null)
  const [summaryData, setSummaryData] = useState({})
  const step2FieldsRef = useRef(null)
  const mode = "guideBooking"
  const isExistingClient = useWatch("isExistingClient", form)
  const paymentType = useWatch("paymentType", form)
  const adjustedPrice = useWatch("adjustedPrice", form)
  const depositOnly = useWatch("depositOnly", form)
  const depositPercent = useWatch("depositPercent", form)
  const customDepositAmount = useWatch("customDepositAmount", form)
  const depositFraction = getCardFeeFraction({ depositOnly, depositPercent, customDepositAmount })
  const [alertMessage, setAlertMessage] = useState("")
  const isSearching = useSelector((state) => state.bookings.existing_clients_list.loading)
  const assetAllocation = useSelector((state) => state.bookings.assetAllocation)
  const {
    experienceById: { experienceDetail }
  } = useSelector((state) => state.experiences)

  const isEdit = !!bookingUuid
  // A booking discounted to nothing has no charge to place, so asking how to take it is noise: the
  // whole payment-method card is dropped and the booking saves as cash (see submitTripBooking,
  // which also keeps the card and the Stripe intent out of it).
  const hasNothingToCollect = Number(bookingSelections?.amount ?? 0) <= 0
  // This form is the create screen; `isEdit` is true only for book-again, which carries the source
  // booking's discount forward. Once the guide picks a different experience that discount no longer
  // belongs to the booking, so it must stop feeding the breakdown and the totals.
  // preserve: the experience Select unmounts on step 2, and a plain useWatch would then report no
  // experience at all — see the same note in BookingWindow.
  const selectedExperienceUuid = useWatch("experience", { form, preserve: true })?.value
  const isExperienceChanged =
    isEdit && !!booking?.trip?.uuid && !!selectedExperienceUuid && selectedExperienceUuid !== booking.trip.uuid

  const handleAddOnsChange = useCallback(
    ({ target: { value: selections } }) => {
      const addOnsTotal = calculateAddOnsTotal(selections, experienceDetail?.add_ons)
      const counts = bookingSelections?.counts
      const tripLength = bookingSelections?.tripLength
      const hasGuestCounts =
        counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
      const update = { addOns: selections, addOnsTotal }
      if (hasGuestCounts && tripLength) {
        const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
        const overrideGuestSubtotal = adjustedPrice != null && adjustedPrice > 0 ? adjustedPrice : undefined
        const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(
          { ...bookingSelections, addOns: selections, addOnsTotal },
          counts,
          guideFeePercent,
          {
            paymentType,
            isEdit,
            originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
            customFees: experienceDetail?.assigned_custom_fees,
            platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
            addOnsCatalog: experienceDetail?.add_ons,
            discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit && !isExperienceChanged),
            dayAdjustments: experienceDetail?.day_adjustments,
            seasonalAdjustments: experienceDetail?.seasonal_adjustments,
            seasonRanges: experienceDetail?.available_date_ranges,
            depositFraction,
            overrideGuestSubtotal
          }
        ) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }
        update.amount = total
        update.baseAmount = guestSubtotal
        update.originalBaseAmount = calculatedGuestSubtotal
        update.breakdown = breakdown
        update.preCardTotal = preCardTotal
        update.cardProcessingFee = cardProcessingFee
      }
      updateBookingSelections(update)
    },
    [updateBookingSelections, experienceDetail, booking, bookingSelections, paymentType, isEdit, isExperienceChanged, appliedPromos, depositFraction, adjustedPrice]
  )

  useEffect(() => {
    const counts = bookingSelections?.counts
    const tripLength = bookingSelections?.tripLength
    const hasGuestCounts =
      counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
    if (!hasGuestCounts || !tripLength) return
    const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
    const overrideGuestSubtotal = adjustedPrice != null && adjustedPrice > 0 ? adjustedPrice : undefined
    const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(bookingSelections, counts, guideFeePercent, {
      paymentType,
      isEdit,
      originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
      customFees: experienceDetail?.assigned_custom_fees,
      platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
      addOnsCatalog: experienceDetail?.add_ons,
      discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit && !isExperienceChanged),
      dayAdjustments: experienceDetail?.day_adjustments,
      seasonalAdjustments: experienceDetail?.seasonal_adjustments,
      seasonRanges: experienceDetail?.available_date_ranges,
      depositFraction,
      overrideGuestSubtotal
    }) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }
    if (total !== bookingSelections?.amount) {
      updateBookingSelections({ amount: total, baseAmount: guestSubtotal, originalBaseAmount: calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedPromos])

  // The card processing fee is charged only on the deposit (pay-now) portion, so changing the
  // deposit % or payment type on step 2 must recompute the total. ExperienceDetail (which recalcs
  // on step 1) is unmounted on step 2, so handle the deposit/payment-type change here.
  useEffect(() => {
    const counts = bookingSelections?.counts
    const tripLength = bookingSelections?.tripLength
    const hasGuestCounts =
      counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
    if (!hasGuestCounts || !tripLength) return
    const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
    const overrideGuestSubtotal = adjustedPrice != null && adjustedPrice > 0 ? adjustedPrice : undefined
    const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(bookingSelections, counts, guideFeePercent, {
      paymentType,
      isEdit,
      originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
      customFees: experienceDetail?.assigned_custom_fees,
      platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
      addOnsCatalog: experienceDetail?.add_ons,
      discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit && !isExperienceChanged),
      dayAdjustments: experienceDetail?.day_adjustments,
      seasonalAdjustments: experienceDetail?.seasonal_adjustments,
      seasonRanges: experienceDetail?.available_date_ranges,
      depositFraction,
      overrideGuestSubtotal
    }) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }
    if (total !== bookingSelections?.amount) {
      updateBookingSelections({ amount: total, baseAmount: guestSubtotal, originalBaseAmount: calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositFraction, paymentType])

  // Subtotal adjustment: recompute when the negotiated price changes. The override is read by
  // calculateBookingTotal via overrideGuestSubtotal; originalBaseAmount keeps the computed value
  // so PriceBreakdown can show the struck-through original.
  useEffect(() => {
    const counts = bookingSelections?.counts
    const tripLength = bookingSelections?.tripLength
    const hasGuestCounts =
      counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
    if (!hasGuestCounts || !tripLength) return
    const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
    const overrideGuestSubtotal = adjustedPrice != null && adjustedPrice > 0 ? adjustedPrice : undefined
    const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(bookingSelections, counts, guideFeePercent, {
      paymentType,
      isEdit,
      originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
      customFees: experienceDetail?.assigned_custom_fees,
      platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
      addOnsCatalog: experienceDetail?.add_ons,
      discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit && !isExperienceChanged),
      dayAdjustments: experienceDetail?.day_adjustments,
      seasonalAdjustments: experienceDetail?.seasonal_adjustments,
      seasonRanges: experienceDetail?.available_date_ranges,
      depositFraction,
      overrideGuestSubtotal
    }) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }
    updateBookingSelections({ amount: total, baseAmount: guestSubtotal, originalBaseAmount: calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustedPrice])

  const handleContinue = async () => {
    const clientFields = isExistingClient ? STEP1_EXISTING_CLIENT_FIELDS : STEP1_NEW_CLIENT_FIELDS
    const accessibilityRequired = form.getFieldValue("accessibility_required")
    const fieldsToValidate = [...clientFields, ...STEP1_BASE_FIELDS, ...(accessibilityRequired ? ["accessibility_notes"] : [])]
    try {
      await form.validateFields(fieldsToValidate)
      if (alertMessage) return
      // Don't allow proceeding when asset allocation failed (e.g. insufficient capacity).
      if (assetAllocation?.error) return
      setSummaryData(form.getFieldsValue(true))
      setCurrentStep(2)
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
    } catch {}
  }

  useEffect(() => {
    if (currentStep === 2 && step2FieldsRef.current) {
      form.setFieldsValue(step2FieldsRef.current)
    }
  }, [currentStep, form])

  const clientDisplayName = summaryData.isExistingClient
    ? summaryData.existing_client?.label
    : [summaryData.firstName, summaryData.lastName].filter(Boolean).join(" ")
  const summaryEmail = summaryData.email
  const experienceName = summaryData.experience?.label || experienceDetail?.title

  return (
    <Spin spinning={isSearching}>
      <CustomForm layout="vertical" form={form} onValuesChange={onValuesChange}>
        <Stepper activeStep={currentStep} t={t} />
        <div className={styles.scrollContainer} ref={scrollContainerRef}>
          {currentStep === 1 ? (
            <>
              <Card className={styles.cardContainer}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.client_detail")}
                </Title>
                <ClientDetail
                  setExistingClientId={setExistingClientId}
                  form={form}
                  bookingId={bookingUuid}
                  alertMessage={alertMessage}
                  setAlertMessage={setAlertMessage}
                  booking={booking}
                />
              </Card>
              <Card className={styles.cardContainer}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.experience_details")}
                </Title>
                <ExperienceDetail
                  form={form}
                  bookingId={bookingUuid}
                  booking={booking}
                  bookAgain={bookAgain}
                  mode={mode}
                  filterExperienceIds={filterExperienceIds}
                  appliedPromos={appliedPromos}
                  bookingSelections={bookingSelections}
                  updateBookingSelections={updateBookingSelections}
                />
              </Card>
            </>
          ) : (
            <>
              <Card className={styles.cardContainer}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.booking_summary", "Booking Summary")}
                </Title>
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={12}>
                    <Text fw={600} type="secondary">
                      {t("booking.labels.client_name", "Client Name")}:{" "}
                    </Text>
                    <Text>{clientDisplayName || "—"}</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text fw={600} type="secondary">
                      {t("booking.labels.client_email", "Client Email")}:{" "}
                    </Text>
                    <Text>{summaryEmail || "—"}</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text fw={600} type="secondary">
                      {t("booking.labels.experience")}:{" "}
                    </Text>
                    <Text>{experienceName || "—"}</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text fw={600} type="secondary">
                      {t("booking.labels.booked_date")}:{" "}
                    </Text>
                    {bookingSelections?.date ? (
                      <>
                        <Text>{dayjs(bookingSelections.date).format("MM/DD/YYYY")}</Text>
                        {bookingSelections?.timeSlot && <Text type="secondary"> · {bookingSelections.timeSlot}</Text>}
                      </>
                    ) : (
                      <Text>—</Text>
                    )}
                  </Col>
                </Row>
              </Card>
              <Card className={styles.cardContainer} style={{ display: hasNothingToCollect ? "none" : undefined }}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.payment_method")}
                </Title>
                <PaymentMethod
                  existingClientId={existingClientId}
                  setExistingClientId={setExistingClientId}
                  form={form}
                  bookingId={bookingUuid}
                  booking={booking}
                  bookAgain={bookAgain}
                  isPaymentRequired={!hasNothingToCollect}
                  appliedPromos={appliedPromos}
                  bookingSelections={bookingSelections}
                  updateBookingSelections={updateBookingSelections}
                />
              </Card>
              <Card className={styles.cardContainer}>
                <Title className={styles.cardTitle} level={5}>
                  {t("booking.price_breakdown")}
                </Title>
                {experienceDetail?.add_ons?.some((a) => a.enabled !== false) && (
                  <AddOnsSelector addOns={experienceDetail.add_ons} selections={bookingSelections?.addOns} onChange={handleAddOnsChange} />
                )}
                <PromoCodeInput
                  totalAmount={bookingSelections?.baseAmount ?? 0}
                  addOnsTotal={bookingSelections?.addOnsTotal ?? 0}
                  appliedPromos={appliedPromos}
                  onPromoApplied={onPromoApplied}
                  onPromoRemoved={onPromoRemoved}
                  experienceId={summaryData.experience?.key}
                  clientIdentifier={summaryData.email || summaryData.phone_number?.fullNumber}
                />
                <Form.Item name="adjustedPrice" hidden noStyle>
                  <input type="hidden" />
                </Form.Item>
                <Form.Item name="adjustmentReason" hidden noStyle>
                  <input type="hidden" />
                </Form.Item>
                <PriceBreakdown
                  form={form}
                  booking={booking}
                  appliedPromos={appliedPromos}
                  isEdit={isEdit}
                  isExperienceChanged={isExperienceChanged}
                  bookingSelections={bookingSelections}
                />
              </Card>
            </>
          )}
        </div>
        <Form.Item className={styles.formActions}>
          <Space>
            {currentStep === 1 ? (
              <>
                <Button variant="outlined" color="primary" htmlType="button" onClick={() => onCancelModal()}>
                  {t("global.cancel")}
                </Button>
                <Button type="primary" htmlType="button" onClick={handleContinue} disabled={assetAllocation?.error}>
                  {t("booking.continue_to_payment", "Continue to Payment")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  htmlType="button"
                  onClick={() => {
                    onClearPromos?.()
                    setCurrentStep(1)
                  }}
                >
                  {t("global.back", "Back")}
                </Button>
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={async () => {
                    try {
                      // Nothing to collect means no method to choose, so the hidden (still
                      // required) field must not block the save.
                      if (!hasNothingToCollect) await form.validateFields(["paymentType"])
                      onSubmit()
                    } catch {}
                  }}
                >
                  {bookingUuid && !bookAgain ? t("global.update") : t("global.submit")}
                </Button>
              </>
            )}
          </Space>
        </Form.Item>
      </CustomForm>
    </Spin>
  )
}

BookingForm.propTypes = {
  form: PropTypes.object.isRequired,
  bookingUuid: PropTypes.string.isRequired,
  bookAgain: PropTypes.object.isRequired,
  booking: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancelModal: PropTypes.func.isRequired,
  filterExperienceIds: PropTypes.arrayOf(PropTypes.number),
  appliedPromos: PropTypes.array.isRequired,
  onPromoApplied: PropTypes.func.isRequired,
  onPromoRemoved: PropTypes.func.isRequired,
  onClearPromos: PropTypes.func,
  onValuesChange: PropTypes.func,
  bookingSelections: PropTypes.object.isRequired,
  updateBookingSelections: PropTypes.func.isRequired
}

export default BookingForm
